import { QueryClient } from '@tanstack/react-query';

/**
 * React Query Client Configuration
 * Optimized for mobile app with sensible defaults
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000, // Previously called cacheTime
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * Query Keys Factory
 * Centralized query key management for consistency
 */
export const queryKeys = {
  // Products
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.products.lists(), filters] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (isin: string) => [...queryKeys.products.details(), isin] as const,
    priceHistory: (isin: string, period: string) =>
      [...queryKeys.products.detail(isin), 'price-history', period] as const,
    underlyingsHistory: (isin: string, period: string) =>
      [...queryKeys.products.detail(isin), 'underlyings-history', period] as const,
    earlyRedemption: (isin: string) =>
      [...queryKeys.products.detail(isin), 'early-redemption'] as const,
  },

  customReports: {
    all: ['custom-reports'] as const,
    list: () => [...queryKeys.customReports.all, 'list'] as const,
    colleagues: () => [...queryKeys.customReports.all, 'colleagues'] as const,
    detail: (id: string) => [...queryKeys.customReports.all, 'detail', id] as const,
  },

  // Events (weekStart = Monday YYYY-MM-DD, any week of the year)
  events: {
    all: ['events'] as const,
    dashboard: (weekStart: string) => [...queryKeys.events.all, 'dashboard', weekStart] as const,
    list: (weekStart: string) => [...queryKeys.events.all, 'list', weekStart] as const,
    upcoming: (
      fromDate: string,
      toDate: string,
      includeWarrants: boolean,
      ownershipScope: 'group' | 'self'
    ) =>
      [...queryKeys.events.all, 'upcoming', fromDate, toDate, includeWarrants, ownershipScope] as const,
    calendar: (
      fromDate: string,
      toDate: string,
      includeWarrants: boolean,
      ownershipScope: 'group' | 'self'
    ) =>
      [...queryKeys.events.all, 'calendar', fromDate, toDate, includeWarrants, ownershipScope] as const,
  },

  // Statistics
  statistics: {
    all: ['statistics'] as const,
    issuerRanking: (filterType: string, includeWarrants: boolean) =>
      [...queryKeys.statistics.all, 'issuer-ranking', filterType, includeWarrants] as const,
    currencyRanking: (filterType: string, includeWarrants: boolean) =>
      [...queryKeys.statistics.all, 'currency-ranking', filterType, includeWarrants] as const,
    userStats: (includeWarrants: boolean) =>
      [...queryKeys.statistics.all, 'user-stats', includeWarrants] as const,
    topExposure: (filterType: string, includeWarrants: boolean) =>
      [...queryKeys.statistics.all, 'top-exposure', filterType, includeWarrants] as const,
  },

  // Expiry
  expiry: {
    all: ['expiry'] as const,
    data: (includeWarrants: boolean) => [...queryKeys.expiry.all, 'data', includeWarrants] as const,
    products: (quarter: string, includeWarrants: boolean) =>
      [...queryKeys.expiry.all, 'products', quarter, includeWarrants] as const,
  },

  // User
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
    layout: () => [...queryKeys.dashboard.all, 'layout'] as const,
  },

  // Favourites / watchlist
  favourites: {
    all: ['favourites'] as const,
    lists: () => [...queryKeys.favourites.all, 'list'] as const,
    list: () => [...queryKeys.favourites.lists()] as const,
    watchlists: () => [...queryKeys.favourites.all, 'watchlist'] as const,
    watchlist: (ownershipScope: 'group' | 'self' = 'group') =>
      [...queryKeys.favourites.watchlists(), ownershipScope] as const,
  },
};
